import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, DollarSign, Calendar, TrendingUp, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { getAssetById } from '../data/mockAssets';

const AssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);

  useEffect(() => {
    const foundAsset = getAssetById(id);
    setAsset(foundAsset);
  }, [id]);

  if (!asset) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a1628] to-[#0f1f3a] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  const handleApplyForLoan = () => {
    const user = localStorage.getItem('user');
    if (!user) {
      navigate('/login', { state: { from: `/asset/${id}` } });
    } else {
      navigate(`/apply/${id}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1e293b] to-[#0f172a]">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <Button 
            onClick={() => navigate('/marketplace')}
            variant="outline"
            className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Marketplace
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Section */}
            <div>
              <div className="relative h-96 rounded-lg overflow-hidden mb-4">
                <img 
                  src={asset.image} 
                  alt={asset.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4">
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    asset.status === 'Available' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-black'
                  }`}>
                    {asset.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div>
              <div className="text-sm text-blue-400 font-medium mb-2">{asset.type}</div>
              <h1 className="text-4xl font-bold text-white mb-4">{asset.name}</h1>
              <div className="flex items-center text-gray-300 mb-6">
                <MapPin className="h-5 w-5 mr-2" />
                <span className="text-lg">{asset.location}</span>
              </div>

              <div className="bg-[#0f1f3a] border border-blue-500/30 rounded-lg p-6 mb-6">
                <div className="text-sm text-gray-400 mb-2">Estimated Value</div>
                <div className="text-4xl font-bold text-white mb-4">
                  ${asset.estimatedValue.toLocaleString()}
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-400">Max Loan (70% LTV)</div>
                    <div className="text-white font-semibold text-lg">
                      ${(asset.estimatedValue * 0.7).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400">Rate</div>
                    <div className="text-white font-semibold text-lg">9.95%</div>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleApplyForLoan}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6 text-lg mb-6"
              >
                Apply for Loan
              </Button>

              <div className="bg-[#0f1f3a] border border-blue-500/30 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Asset Details</h3>
                <div className="space-y-3">
                  {asset.category === 'cre' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Square Footage</span>
                        <span className="text-white font-semibold">{asset.sqft.toLocaleString()} sq ft</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Year Built</span>
                        <span className="text-white font-semibold">{asset.yearBuilt}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Occupancy Rate</span>
                        <span className="text-white font-semibold">{asset.occupancyRate}%</span>
                      </div>
                    </>
                  )}
                  {asset.category === 'foreclosure' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Bedrooms</span>
                        <span className="text-white font-semibold">{asset.bedrooms}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Bathrooms</span>
                        <span className="text-white font-semibold">{asset.bathrooms}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Square Footage</span>
                        <span className="text-white font-semibold">{asset.sqft.toLocaleString()} sq ft</span>
                      </div>
                    </>
                  )}
                  {asset.category === 'airplane' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Manufacturer</span>
                        <span className="text-white font-semibold">{asset.manufacturer}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Year Manufactured</span>
                        <span className="text-white font-semibold">{asset.yearManufactured}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Flight Hours</span>
                        <span className="text-white font-semibold">{asset.totalFlightHours.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Seating Capacity</span>
                        <span className="text-white font-semibold">{asset.seatingCapacity}</span>
                      </div>
                    </>
                  )}
                  {asset.category === 'company' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Industry</span>
                        <span className="text-white font-semibold">{asset.industry}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Employees</span>
                        <span className="text-white font-semibold">{asset.employees}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Annual Revenue</span>
                        <span className="text-white font-semibold">${(asset.annualRevenue / 1000000).toFixed(1)}M</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Year Established</span>
                        <span className="text-white font-semibold">{asset.yearEstablished}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-[#0f1f3a] border border-blue-500/30 rounded-lg p-6">
            <h3 className="text-2xl font-bold text-white mb-4">Description</h3>
            <p className="text-gray-300 leading-relaxed">{asset.description}</p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AssetDetail;
